

import React, { useState } from 'react';
import { Box, ListItemAvatar, ListItemText, Typography, Avatar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import { StatusChip, StatusButton } from './styles'
import { IWorkerUser } from '@/modules/worker';
import SecureAvatar from '../common/SecureAvatar';
import ActionPopup from '../common/ActionPopup';

interface WorkerStatusProps {
  user: IWorkerUser;
  id: number;
  status: string | undefined;
  showActionButton?: boolean | undefined;
  onClickButton?: (status: string, reason?: string) => void;
}

const WorkerStatus: React.FC<WorkerStatusProps> = ({ user, id, status, showActionButton = false, onClickButton }) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [showSuspendPopup, setShowSuspendPopup] = useState(false);

  const getWorkerProfilePicture = () => {
    if (user.profile_picture) {
      // console.log(ASSET_URL) 
      return (<SecureAvatar
        sx={{
          bgcolor: deepOrange[500],
          borderRadius: 3,
          width: 56, // Slightly reduced for density
          height: 56,
          objectFit: 'cover',
        }}
        src={user.profile_picture} />)
    } else {
      return (<Avatar sx={{ 
        bgcolor: deepOrange[500], 
        borderRadius: 2,
        width: 56,
        height: 56,
        fontSize: '1.5rem'
      }}>{user.first_name[0]}</Avatar>)
    }
  }

  const getSatusColor = (status: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
    if (status === 'active' || status === 'approved') {
      return 'success';
    } else if (status === 'pending') {
      return 'warning';
    } else if (status === 'blocked') {
      return 'error';
    } else {
      return 'default';
    }
  }

  const handleConfirmOpen = () => {
    if (status === "active") {
      setShowSuspendPopup(true);
    } else {
      setOpenConfirm(true);
    }
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
    setShowSuspendPopup(false);
  };

  const handleReactivate = () => {
    onClickButton && onClickButton("accept");
    handleConfirmClose();
  }

  const handleSuspend = (reason?: string) => {
    onClickButton && onClickButton("suspend", reason);
    handleConfirmClose();
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ListItemAvatar sx={{ minWidth: '70px' }}> {/* Reduced minWidth to bring text closer */}
        {getWorkerProfilePicture()}
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="h6" fontWeight="600" sx={{ mb: -0.5 }}>
            {user.first_name + " " + user.last_name}
          </Typography>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{
                marginRight: 1.5,
                lineHeight: '1.2',
                fontSize: '0.9rem'
              }}
            >
              {`ID: ${id}`}
            </Typography>
            {status &&
              <StatusChip
                status={(status) ? status : "Pending"}
                label={status}
                size="small"
                color={getSatusColor(status)}
              />
            }

          </Box>
        }
        sx={{ marginLeft: 0.5 }} // Significantly reduced from 2
      />
      {
        showActionButton && status !== 'pending' &&
        <Box sx={{ flexGrow: 1 }} flexShrink={0} textAlign="right">
          <StatusButton
            variant="outlined"
            size="small"
            color='error'
            status={status ? status : 'Pending'}
            onClick={handleConfirmOpen}
          >
            {(status === "active") ? "Suspend" : "Reactivate"}
          </StatusButton>
          <Dialog
            open={openConfirm}
            onClose={handleConfirmClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {(status === "active") ? "Suspend Worker?" : "Reactivate Worker?"}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to {(status === "active") ? "suspend" : "reactivate"} this worker?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleConfirmClose}>Cancel</Button>
              <Button onClick={handleReactivate} autoFocus color="primary">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          <ActionPopup
            open={showSuspendPopup}
            onClose={handleConfirmClose}
            onSubmit={(reason) => handleSuspend(reason)}
            title="Suspend Worker"
            message="Are you sure you want to suspend this worker? Please provide a reason."
            submitButtonText="Suspend"
            textFieldLabel="Reason"
            textFieldPlaceholder="Enter reason for suspension"
            required={true}
          />
        </Box>
      }
    </Box>
  );
}

export default WorkerStatus;